'use client'

import React, {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
} from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { ArrowLeft, ArrowRight, ArrowUpRight, PlayCircle, RotateCw } from 'lucide-react'
import { STOREFRONTS } from '@/lib/copy/storefronts'

// Intersection-observer hook — fires once when the element enters the viewport.
function useInViewOnce(rootMargin = '200px') {
  const ref = useRef<HTMLElement | null>(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          obs.disconnect()
        }
      },
      { rootMargin },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [rootMargin])
  return { ref, visible }
}

// "Live on real storefronts" band. Real Idukki customers, each card
// linking out to the actual storefront so visitors can see the widget
// live. Honest by design: real brand logos, no invented screenshots,
// no fake metrics.
//
// Mobile (< lg): a drag-to-cycle card stack — the top storefront is
// foregrounded with the next two peeking behind it as physical cards.
// Drag/swipe horizontally past ~80px (or tap Next) to cycle the deck.
// Uses the exact same gesture/animation as the home "Same data model.
// Every surface a shopper meets" strip (use-case-strip.tsx), with big
// image-led cards, ending with the "More DTC brands" CTA tile.
// Desktop (lg+): a single horizontal snap-scroll row ("ticker of live
// customers") with BIGGER cards — only ~2–3 visible at a time so the live
// store image reads prominently. Every real storefront plus a final
// "More DTC brands" CTA tile sit in one band, reachable by scrolling
// sideways, with a right-edge fade and centered prev/next arrow buttons
// overlaid on the row.

export type Storefront = {
  name: string
  url: string
  industry: string
  usesIdukkiFor: string
  /** Short widget-type chip rendered on the card (e.g. "Shoppable wall"). */
  widgetType: string
  /** First-letter fallback when no logo asset exists. */
  initial: string
  /** Real brand logo from public/brand/customer-logos/, when we have it. */
  logo?: string
  /** Single screenshot or GIF of the live widget. */
  screenshot?: string
  /** Multiple screenshots/GIFs — renders as an auto-cycling carousel. */
  screenshots?: string[]
  /** Previous platform this customer migrated from (e.g. 'Bazaarvoice'). */
  replacedFrom?: string
}

// Canonical storefront data lives in lib/copy/storefronts.ts (shared with
// the /videos watch pages and the video sitemap); adapt it to this
// component's screenshot-based shape.
const STORES_FROM_REGISTRY: Storefront[] = STOREFRONTS.map((s) => ({
  name: s.name,
  url: s.url,
  industry: s.industry,
  usesIdukkiFor: s.usesIdukkiFor,
  widgetType: s.widgetType,
  initial: s.initial,
  logo: s.logo,
  replacedFrom: s.replacedFrom,
  ...(s.videos.length > 1
    ? { screenshots: s.videos.map((v) => v.src) }
    : { screenshot: s.videos[0]?.src }),
}))

const DOT_TEXTURE = {
  backgroundImage:
    'radial-gradient(circle at 1px 1px, hsl(var(--foreground) / 0.07) 1px, transparent 0)',
  backgroundSize: '15px 15px',
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(mq.matches)
    const onChange = () => setReduced(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return reduced
}

export function LiveOnStores() {
  return (
    <section className="py-20 md:py-28">
      <div className="container-wide">
        <div className="max-w-2xl">
          <h2 className="text-balance font-display text-3xl font-semibold tracking-tight md:text-4xl">
            Idukki widgets are{' '}
            <span className="display-italic">running on real revenue right now</span>.
          </h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
            These aren&rsquo;t mockups. They&rsquo;re idukki-powered widgets on production
            storefronts across fashion, beauty, footwear, streetwear and home improvement. Tap any
            tile to see the widget live.
          </p>
          <Link
            href="/videos"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary underline-offset-4 hover:underline"
          >
            <PlayCircle className="h-4 w-4" /> Watch every widget capture
          </Link>
        </div>
      </div>

      {/* Desktop (lg+): one horizontal snap-scroll ticker with BIGGER
          cards — only ~2–3 show at the container width so each live store
          image reads prominently. Every real storefront plus the "More DTC
          brands" tile sit in one band, reachable by scrolling sideways.
          Hidden below lg; the mobile carousel below owns < lg exclusively. */}
      <div className="hidden lg:block">
        <StoreTicker stores={STORES_FROM_REGISTRY} />
      </div>

      {/* Mobile (< lg): a drag-to-cycle card stack reusing the exact
          gesture/animation from the home "Every surface a shopper meets"
          strip. Same data as the ticker, ending with the "More DTC
          brands" tile. Hidden at lg+; the desktop ticker above owns lg+
          exclusively. */}
      <div className="lg:hidden">
        <MobileStoreStack stores={STORES_FROM_REGISTRY} />
      </div>
    </section>
  )
}

function StoreTicker({ stores }: { stores: Storefront[] }) {
  const items: (Storefront | null)[] = [...stores, null]
  const scrollRef = useRef<HTMLUListElement>(null)
  const reducedMotion = usePrefersReducedMotion()

  function scrollBy(dir: 1 | -1) {
    scrollRef.current?.scrollBy({ left: dir * 496, behavior: reducedMotion ? 'auto' : 'smooth' })
  }

  return (
    <div className="container-wide">
      <div className="relative mt-10">
        {/* Right-edge fade hint */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-background to-transparent"
        />

        <button
          type="button"
          onClick={() => scrollBy(-1)}
          aria-label="Scroll storefronts left"
          className="absolute left-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border bg-card/90 text-foreground shadow-md backdrop-blur transition hover:bg-card hover:shadow-lg"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => scrollBy(1)}
          aria-label="Scroll storefronts right"
          className="absolute right-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border bg-card/90 text-foreground shadow-md backdrop-blur transition hover:bg-card hover:shadow-lg"
        >
          <ArrowRight className="h-4 w-4" />
        </button>

        {/* Manual snap-scroll row — no auto-advance, videos only load when in view */}
        <ul
          ref={scrollRef}
          className="no-scrollbar flex snap-x snap-mandatory gap-6 overflow-x-auto pb-4"
          style={{ scrollPaddingLeft: '0px' }}
        >
          {items.map((s, i) => (
            <li key={i} className="w-[30rem] max-w-[88vw] shrink-0 snap-start">
              {s ? <StoreCard s={s} large /> : <MoreBrandsTile />}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

function MobileStoreStack({ stores }: { stores: Storefront[] }) {
  const reducedMotion = usePrefersReducedMotion()
  const count = stores.length + 1
  const [topIndex, setTopIndex] = useState(0)
  const [drag, setDrag] = useState(0)
  // Was derived as `startX.current !== null` — a ref read during render,
  // which react-hooks' `refs` rule (eslint-plugin-react-hooks v7) now flags:
  // ref mutations don't trigger re-renders, so a value read straight from a
  // ref during render can go stale. Track it as real state instead; the ref
  // itself stays ref-only, read only inside the pointer handlers below.
  const [dragging, setDragging] = useState(false)
  const startX = useRef<number | null>(null)

  const next = () => setTopIndex((i) => (i + 1) % count)

  function onPointerDown(e: PointerEvent<HTMLDivElement>) {
    startX.current = e.clientX
    setDragging(true)
    ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
  }
  function onPointerMove(e: PointerEvent<HTMLDivElement>) {
    if (startX.current === null) return
    setDrag(e.clientX - startX.current)
  }
  function onPointerEnd() {
    if (Math.abs(drag) > 80) next()
    startX.current = null
    setDrag(0)
    setDragging(false)
  }

  const visible = [0, 1, 2].map((offset) => (topIndex + offset) % count)
  const renderSlide = (index: number) =>
    index < stores.length ? <StoreCard s={stores[index]} large /> : <MoreBrandsTile />

  return (
    <div className="mt-10">
      <div
        className="relative mx-auto h-[34rem] w-full max-w-sm select-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        style={{ touchAction: 'pan-y' }}
      >
        {visible.map((index, offset) => {
          const isTop = offset === 0
          const spring = reducedMotion ? 'none' : 'transform 280ms cubic-bezier(0.22, 1, 0.36, 1)'
          const baseStyle: CSSProperties = isTop
            ? {
                transform: reducedMotion
                  ? `translate3d(${drag}px, 0, 0)`
                  : `translate3d(${drag}px, 0, 0) rotate(${drag * 0.05}deg)`,
                transition: dragging ? 'none' : spring,
                opacity: 1,
                zIndex: 30,
              }
            : {
                transform: `translateY(${offset * 14}px) scale(${1 - offset * 0.04}) rotate(${offset === 1 ? -2 : 2}deg)`,
                transition: reducedMotion
                  ? 'none'
                  : 'transform 280ms cubic-bezier(0.22, 1, 0.36, 1), opacity 200ms',
                opacity: 1 - offset * 0.18,
                zIndex: 30 - offset,
                pointerEvents: 'none',
              }
          return (
            <div
              key={`${index}-${offset}-${topIndex}`}
              className="absolute inset-x-0 top-0 h-full will-change-transform"
              style={baseStyle}
            >
              <div className="h-full">{renderSlide(index)}</div>
            </div>
          )
        })}
      </div>

      <div className="mt-6 flex items-center justify-center gap-3">
        <span className="font-mono text-xs tabular-nums text-muted-foreground">
          {String(topIndex + 1).padStart(2, '0')}{' '}
          <span className="text-muted-foreground">/ {String(count).padStart(2, '0')}</span>
        </span>
        <button
          type="button"
          onClick={next}
          aria-label="Next storefront"
          className="inline-flex h-9 items-center gap-1.5 rounded-full bg-primary px-4 text-xs font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/85"
        >
          Next
          <RotateCw className="h-3 w-3" />
        </button>
      </div>
      <div aria-hidden className="mt-3 flex justify-center gap-1.5">
        {Array.from({ length: count }).map((_, i) => (
          <span
            key={i}
            className={`h-1.5 rounded-full transition-[width,background-color] ${i === topIndex ? 'w-5 bg-primary' : 'w-1.5 bg-foreground/20'}`}
          />
        ))}
      </div>
    </div>
  )
}

function MediaItem({
  src,
  alt,
  className,
  style,
}: {
  src: string
  alt: string
  className?: string
  style?: CSSProperties
}) {
  // Called unconditionally (Rules of Hooks) — cheap no-op for the <img>
  // branch below, since its ref never attaches to anything.
  const { ref, visible } = useInViewOnce('100px')
  if (src.endsWith('.webm') || src.endsWith('.mp4')) {
    // Only set the video src once the element scrolls into view so off-screen
    // cards never download their webm files. preload="none" prevents the
    // browser from fetching metadata for not-yet-visible videos.
    return (
      <video
        ref={ref as React.RefObject<HTMLVideoElement>}
        src={visible ? src : undefined}
        autoPlay
        muted
        loop
        playsInline
        preload="none"
        aria-label={alt}
        className={className}
        style={style}
      />
    )
  }
  return (
    /* eslint-disable-next-line @next/next/no-img-element */
    <img src={src} alt={alt} loading="lazy" className={className} style={style} />
  )
}

function ScreenshotCarousel({
  images,
  alt,
  large = false,
}: {
  images: string[]
  alt: string
  large?: boolean
}) {
  const [active, setActive] = useState(0)
  const multi = images.length > 1
  const heightClass = large ? 'h-60 sm:h-64' : 'h-28'

  // No auto-advance — user controls via dot buttons or swipe.
  // Only the active image's MediaItem is rendered so off-screen
  // screenshots don't download their webm files.

  return (
    <div className="relative overflow-hidden border-y border-border/60">
      <MediaItem
        key={images[active]}
        src={images[active]}
        alt={`${alt} widget${multi ? ` ${active + 1}` : ''}`}
        className={`${heightClass} w-full object-cover object-top`}
      />
      {multi && (
        <div className="absolute bottom-0 left-0 right-0 flex justify-center">
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={(e) => {
                e.preventDefault()
                setActive(i)
              }}
              aria-label={`Show screenshot ${i + 1}`}
              className="flex h-6 w-6 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
            >
              <span
                aria-hidden
                className={`h-1 rounded-full transition-[width,background-color] ${
                  i === active ? 'w-4 bg-white' : 'w-1 bg-white/50'
                }`}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function StoreCard({ s, large = false }: { s: Storefront; large?: boolean }) {
  return (
    <a
      href={s.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex h-full flex-col overflow-hidden rounded-2xl border bg-card transition hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5"
    >
      <div
        className={`relative flex shrink-0 items-center justify-center bg-muted ${
          large ? 'h-28' : 'h-24'
        }`}
      >
        <div aria-hidden className="absolute inset-0 opacity-70" style={DOT_TEXTURE} />
        <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-card/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 shadow-sm backdrop-blur">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-1.5 w-1.5 animate-ping rounded-full bg-emerald-500 opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
          </span>
          Live
        </span>
        <span className="absolute left-3 top-3 inline-flex items-center rounded-full bg-foreground/85 px-2 py-0.5 text-[10px] font-semibold text-background shadow-sm backdrop-blur">
          {s.widgetType}
        </span>
        {s.replacedFrom && (
          <span className="absolute left-3 top-10 inline-flex items-center gap-1 rounded-full bg-[#C8FF52] px-2 py-0.5 text-[9px] font-bold text-[#0F1419] shadow-sm">
            ↗ Replaced {s.replacedFrom}
          </span>
        )}
        <span className="relative flex h-12 w-12 items-center justify-center overflow-hidden rounded-2xl border bg-white shadow-md transition group-hover:scale-105">
          {s.logo ? (
            s.logo.endsWith('.svg') ? (
              /* eslint-disable-next-line @next/next/no-img-element */
              <img
                src={s.logo}
                alt={`${s.name} logo`}
                loading="lazy"
                width={48}
                height={40}
                className="max-h-10 max-w-12 object-contain"
              />
            ) : (
              // Raster logos go through the image optimizer: several of these
              // source files are 1000px+ wide (ritualistic.png is 89KB) but
              // render inside a 48px chip.
              <Image
                src={s.logo}
                alt={`${s.name} logo`}
                width={48}
                height={40}
                sizes="48px"
                className="max-h-10 max-w-12 object-contain"
              />
            )
          ) : (
            <span className="font-display text-2xl font-semibold text-foreground/70">
              {s.initial}
            </span>
          )}
        </span>
      </div>

      {(s.screenshots ?? (s.screenshot ? [s.screenshot] : null)) && (
        <ScreenshotCarousel images={s.screenshots ?? [s.screenshot!]} alt={s.name} large={large} />
      )}

      <div className={`flex flex-1 flex-col items-start text-left ${large ? 'p-5' : 'p-4'}`}>
        <p
          className={`font-display font-semibold leading-tight text-foreground ${
            large ? 'text-lg' : 'text-base'
          }`}
        >
          {s.name}
        </p>
        <p className="mt-0.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          {s.industry}
        </p>
        <p className="mt-2 line-clamp-1 text-pretty text-sm text-muted-foreground">
          {s.usesIdukkiFor}
        </p>
        <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary">
          See widget live
          <ArrowUpRight className="h-3.5 w-3.5 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </span>
      </div>
    </a>
  )
}

function MoreBrandsTile() {
  return (
    <Link
      href="/customers"
      className="group flex h-full flex-col justify-between overflow-hidden rounded-2xl border border-dashed border-primary/40 bg-secondary p-6 text-left transition hover:-translate-y-1 hover:border-primary/70 hover:shadow-lg hover:shadow-primary/5"
    >
      <div>
        <span className="inline-flex items-center rounded-full bg-foreground/85 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-background">
          More stores
        </span>
        <p className="mt-4 font-display text-2xl font-semibold leading-tight text-foreground">
          More DTC brands run Idukki.
        </p>
        <p className="mt-2 text-pretty text-sm text-muted-foreground">
          On Shopify, BigCommerce, WooCommerce, Wix and custom stacks. Browse the full customer
          index.
        </p>
      </div>
      <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
        Browse the customer index
        <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
      </span>
    </Link>
  )
}
